/**
 * OutlierOS dashboard shell replica (docs/legacy-ui/01-user-dashboard.md §1): collapsible grouped sidebar
 * (260 px; 64 px rail when collapsed or narrower than 1024 px; remembered), a drawer on phones, a 64 px header
 * with brand tile, capsule tabs and icon buttons, ⌘K palette, footer slot (usage meter, upgrade card) and
 * bottom items. Router-agnostic: links are plain hrefs; `onNavigate` intercepts same-app navigation.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronRight, Menu, Moon, PanelLeftClose, PanelLeftOpen, RotateCw, Search, Settings, Sun, X, type LucideIcon } from 'lucide-react';
import { cn } from '../cn';
import { useTheme } from '../theme';
import { CommandPalette, type PaletteItem } from './CommandPalette';

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href?: string;
  onClick?: () => void;
  badge?: number | string | null;
  kbd?: string;
  external?: boolean;
  /** `tag`: a small chip after the label (e.g. "Collab profile" on a tracked creator, decision 91). */
  children?: Array<{ id: string; label: string; href: string; icon?: LucideIcon; tag?: string }>;
}
export interface NavGroup { id: string; label: string; items: NavItem[] }
export interface Workspace { name: string; plan: string; title?: string; subtitle?: string }
export interface HeaderTab { id: string; label: string; icon: LucideIcon; href: string; badge?: number | null }

export interface AppShellProps {
  brand: { name: string; tagline: string; logo: ReactNode };
  /** Sidebar top card: "{name}'s Workspace" / "{plan} Plan" unless `title` / `subtitle` replace them (Admin). */
  workspace: Workspace;
  groups: NavGroup[];
  bottom: NavItem[];
  footer?: ReactNode;
  tabs: HeaderTab[];
  activeId: string;
  activeTab: string | null;
  /** Changes on every navigation: the content area scrolls back to the top. */
  scrollKey: string;
  headerExtra?: ReactNode;
  paletteItems: PaletteItem[];
  onNavigate: (href: string) => void;
  onRefresh: () => void;
  settingsHref: string;
  children: ReactNode;
}

type Go = (item: { href?: string; onClick?: () => void; external?: boolean }) => (e: React.MouseEvent) => void;

const SIDEBAR_KEY = 'vw_sidebar';

function useMedia(query: string): boolean {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return m;
}

function useSidebarState(): [boolean, (v: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === 'collapsed';
    } catch {
      return false;
    }
  });
  return [collapsed, (v) => {
    setCollapsed(v);
    try {
      localStorage.setItem(SIDEBAR_KEY, v ? 'collapsed' : 'expanded');
    } catch {
      /* ignore */
    }
  }];
}

export function AppShell(p: AppShellProps) {
  const [collapsed, setCollapsed] = useSidebarState();
  const narrow = useMedia('(max-width: 1023px)'); // OutlierOS: rail below 1024 px
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [theme, toggleTheme] = useTheme();
  const main = useRef<HTMLElement>(null);
  const rail = collapsed || narrow;
  const nav = useRef(p.onNavigate);
  nav.current = p.onNavigate;

  useEffect(() => {
    main.current?.scrollTo(0, 0);
  }, [p.scrollKey]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      } else if (mod && e.key === ',') {
        e.preventDefault();
        nav.current(p.settingsHref);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [p.settingsHref]);

  const go: Go = (item) => (e) => {
    if (item.onClick) {
      e.preventDefault();
      item.onClick();
      setMobileOpen(false);
      return;
    }
    if (!item.href || item.external || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    setMobileOpen(false);
    nav.current(item.href);
  };
  const groups = useMemo(() => p.groups.map((g) => ({ ...g, items: g.items.map((i) => (i.id === 'search' ? { ...i, onClick: () => setPaletteOpen(true) } : i)) })), [p.groups]);
  const sideProps = { groups, bottom: p.bottom, footer: p.footer, workspace: p.workspace, activeId: p.activeId, go };

  return (
    <div className="flex min-h-screen overflow-hidden bg-background">
      {/* Sidebar: full or rail from tablet width up */}
      <aside className={cn('z-30 hidden h-screen shrink-0 border-r border-border/70 bg-card/60 transition-[width] duration-300 md:block', rail ? 'w-16' : 'w-[260px]')}>
        {rail
          ? <Rail {...sideProps} onExpand={() => (narrow ? setMobileOpen(true) : setCollapsed(false))} />
          : <FullSidebar {...sideProps} onCollapse={() => setCollapsed(true)} />}
      </aside>
      {/* Drawer (phones; and the expanded sidebar on tablets) */}
      {mobileOpen && <Drawer onClose={() => setMobileOpen(false)}><FullSidebar {...sideProps} onClose={() => setMobileOpen(false)} /></Drawer>}

      <div className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
        <header className="z-20 flex h-16 shrink-0 items-center gap-2 border-b border-border bg-card/95 px-4 backdrop-blur-md sm:gap-3 md:px-6">
          <button aria-label="Open menu" className="rounded-xl p-2 text-muted-foreground hover:bg-secondary md:hidden" onClick={() => setMobileOpen(true)}><Menu className="h-5 w-5" /></button>
          <a href="/" onClick={go({ href: '/' })} className="flex items-center gap-2.5">
            {p.brand.logo}
            <span className="hidden leading-tight sm:block">
              <span className="block text-sm font-bold">{p.brand.name}</span>
              <span className="hidden text-[10px] text-muted-foreground xl:block">{p.brand.tagline}</span>
            </span>
          </a>
          {p.tabs.length > 0 && <nav aria-label="Sections" className="ml-2 hidden items-center gap-0.5 rounded-full bg-secondary p-1 text-xs font-semibold md:flex">
            {p.tabs.map((t) => {
              const active = p.activeTab === t.id;
              return (
                <a key={t.id} href={t.href} onClick={go(t)} aria-current={active ? 'page' : undefined}
                  className={cn('flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 transition-colors', active ? 'bg-card font-bold text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                  <t.icon className="h-3.5 w-3.5" />
                  {t.label}
                  {!!t.badge && <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">{t.badge}</span>}
                </a>
              );
            })}
          </nav>}
          <div className="flex-1" />
          <button onClick={() => setPaletteOpen(true)} className="hidden items-center gap-2 rounded-xl border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground shadow-sm hover:bg-secondary lg:flex">
            <Search className="h-3.5 w-3.5" />Search<span className="ml-2 font-mono text-[10px]">⌘K</span>
          </button>
          <button onClick={toggleTheme} aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            className="rounded-xl border border-border bg-card p-2 shadow-sm hover:bg-secondary">
            {theme === 'dark' ? <Sun className="h-4 w-4 fill-amber-500/20 text-amber-500" /> : <Moon className="h-4 w-4 fill-amber-500/20 text-amber-500" />}
          </button>
          <button onClick={p.onRefresh} aria-label="Refresh data" title="Refresh data" className="hidden rounded-xl border border-border bg-card p-2 shadow-sm hover:bg-secondary sm:block"><RotateCw className="h-4 w-4" /></button>
          <a href={p.settingsHref} onClick={go({ href: p.settingsHref })} aria-label="Settings" title="Settings & extension" className="hidden rounded-xl border border-border bg-card p-2 shadow-sm hover:bg-secondary sm:block"><Settings className="h-4 w-4" /></a>
          {p.headerExtra}
        </header>
        <main ref={main} id="main" className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">{p.children}</main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={p.paletteItems} onNavigate={(h) => { setPaletteOpen(false); nav.current(h); }} />
    </div>
  );
}

/** Phone/tablet drawer: Esc closes, focus moves in and returns to the menu button. */
function Drawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('button, a')?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, []);
  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Navigation">
      <div className="absolute inset-0 bg-black/50 animate-fade-in" onClick={onClose} />
      <aside ref={ref} className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] border-r border-border bg-card shadow-2xl">{children}</aside>
    </div>
  );
}

interface SideProps {
  groups: NavGroup[];
  bottom: NavItem[];
  footer?: ReactNode;
  workspace: Workspace;
  activeId: string;
  go: Go;
}

function Rail({ groups, bottom, activeId, go, onExpand }: SideProps & { onExpand: () => void }) {
  const cls = (active: boolean) => cn('relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors', active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5');
  return (
    <div className="flex h-full w-16 flex-col items-center gap-1 py-3">
      <button aria-label="Expand sidebar" title="Expand sidebar" onClick={onExpand} className="mb-2 rounded-lg p-2 text-muted-foreground hover:bg-secondary"><PanelLeftOpen className="h-[18px] w-[18px]" /></button>
      <nav aria-label="Main" className="flex w-full flex-col items-center">
        {groups.map((g, gi) => (
          <div key={g.id} className={cn('flex w-full flex-col items-center gap-1', gi > 0 && 'mt-1 border-t border-border/70 pt-2')}>
            {g.items.map((i) => (
              <a key={i.id} href={i.href ?? '#'} onClick={i.children ? (e) => { e.preventDefault(); onExpand(); } : go(i)} title={i.label} aria-label={i.label}
                target={i.external ? '_blank' : undefined} rel={i.external ? 'noreferrer' : undefined} className={cls(activeId === i.id || !!i.children?.some((c) => c.id === activeId))}>
                <i.icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                {!!i.badge && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary" />}
              </a>
            ))}
          </div>
        ))}
      </nav>
      <div className="mt-auto flex flex-col items-center gap-1 border-t border-border/70 pt-2">
        {bottom.map((i) => (
          <a key={i.id} href={i.href ?? '#'} onClick={go(i)} title={i.label} aria-label={i.label} target={i.external ? '_blank' : undefined} rel={i.external ? 'noreferrer' : undefined} className={cls(activeId === i.id)}>
            <i.icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
          </a>
        ))}
      </div>
    </div>
  );
}

function SideItem({ i, activeId, go, open, onToggle }: { i: NavItem; activeId: string; go: Go; open: boolean; onToggle: () => void }) {
  if (i.children) {
    return (
      <div>
        <button onClick={onToggle} aria-expanded={open}
          className="flex w-full items-center gap-2.5 rounded-[6px] px-2.5 py-[7px] text-[13px] tracking-wide text-muted-foreground hover:bg-black/5 hover:text-foreground dark:hover:bg-white/5">
          <i.icon className="h-4 w-4" strokeWidth={1.5} />
          <span className="flex-1 truncate text-left">{i.label}</span>
          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform duration-300', open && 'rotate-90')} />
        </button>
        <div className={cn('grid transition-[grid-template-rows] duration-300', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
          <div className="overflow-hidden">
            <div className="ml-[18px] mt-0.5 space-y-0.5 border-l border-border pl-2">
              {i.children.map((c) => (
                <a key={c.id} href={c.href} onClick={go(c)} tabIndex={open ? 0 : -1}
                  className={cn('flex items-center gap-2 truncate rounded-[6px] px-2 py-[5px] text-[12.5px]', activeId === c.id ? 'bg-primary/10 font-semibold text-primary' : 'text-muted-foreground hover:bg-black/5 hover:text-foreground dark:hover:bg-white/5')}>
                  {c.icon && <c.icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />}
                  <span className="truncate">{c.label}</span>
                  {c.tag && <span className="ml-auto shrink-0 rounded-full border border-border bg-secondary px-1.5 text-[9px] font-bold text-muted-foreground">{c.tag}</span>}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
  const active = activeId === i.id;
  return (
    <a href={i.href ?? '#'} onClick={go(i)} aria-current={active ? 'page' : undefined} target={i.external ? '_blank' : undefined} rel={i.external ? 'noreferrer' : undefined}
      className={cn('group flex items-center gap-2.5 rounded-[6px] px-2.5 py-[7px] text-[13px] tracking-wide transition-colors', active ? 'bg-primary/10 font-semibold text-primary' : 'text-muted-foreground hover:bg-black/5 hover:text-foreground dark:hover:bg-white/5')}>
      <i.icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
      <span className="flex-1 truncate">{i.label}</span>
      {i.kbd && <span className="hidden font-mono text-[10px] text-muted-foreground group-hover:inline">{i.kbd}</span>}
      {!!i.badge && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary/10 px-1.5 text-[10px] font-bold text-primary">{i.badge}</span>}
    </a>
  );
}

function FullSidebar({ groups, bottom, footer, workspace, activeId, go, onCollapse, onClose }: SideProps & { onCollapse?: () => void; onClose?: () => void }) {
  const [open, setOpen] = useState<Record<string, boolean>>({ tracked: true });
  const item = (i: NavItem) => <SideItem key={i.id} i={i} activeId={activeId} go={go} open={open[i.id] ?? false} onToggle={() => setOpen((o) => ({ ...o, [i.id]: !o[i.id] }))} />;
  return (
    <div className="flex h-full flex-col p-3">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2.5 px-1.5 py-1">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] bg-primary text-sm font-bold text-primary-foreground">{(workspace.name[0] ?? 'W').toUpperCase()}</span>
          <span className="min-w-0 leading-tight">
            <span className="block max-w-[150px] truncate text-[13px] font-semibold">{workspace.title ?? `${workspace.name}'s Workspace`}</span>
            <span className="block max-w-[150px] truncate text-[11px] text-muted-foreground">{workspace.subtitle ?? `${workspace.plan} Plan`}</span>
          </span>
        </div>
        {onClose
          ? <button aria-label="Close menu" onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary"><X className="h-4 w-4" /></button>
          : <button aria-label="Collapse sidebar" title="Collapse sidebar" onClick={onCollapse} className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary"><PanelLeftClose className="h-4 w-4" /></button>}
      </div>
      <nav className="no-scrollbar -mx-1 flex-1 space-y-4 overflow-y-auto px-1" aria-label="Main">
        {groups.map((g) => (
          <div key={g.id}>
            <div className="mb-1 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/50">{g.label}</div>
            <div className="space-y-0.5">{g.items.map(item)}</div>
          </div>
        ))}
      </nav>
      {footer && <div className="space-y-2 pt-3">{footer}</div>}
      <div className="mt-3 space-y-0.5 border-t border-border pt-3">{bottom.map(item)}</div>
    </div>
  );
}
